import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API;

const makeCacheKey = (docDate, companyCode, yearCode) =>
  `${docDate}|${companyCode}|${yearCode}`;

// Backs the eway-bill "Compare Portal Data" tab (GenrateEwayBill.jsx), whose
// data comes from the ComparePortalEwayBillData stored procedure - slow even
// after DB-side indexing, and was being re-run on every tab visit because the
// tab unmounts/remounts on every switch (EWayBillPortalTabs.jsx renders it
// conditionally). Caching the result here means revisiting the same date
// (without a real data-changing event) reads from memory instead of re-running
// the stored procedure.
export const fetchEwayBillCompareData = createAsyncThunk(
  'ewayBillCompare/fetchAll',
  async ({ docDate, companyCode, yearCode }) => {
    const response = await axios.get(`${API_URL}/getMatchingPurchaseewaybills`, {
      params: { doc_date: docDate, Company_Code: companyCode, Year_Code: yearCode },
    });
    return { rows: response.data, cacheKey: makeCacheKey(docDate, companyCode, yearCode) };
  },
  {
    condition: ({ docDate, companyCode, yearCode, force }, { getState }) => {
      if (force) return true;
      const state = getState().ewayBillCompare;
      if (state.status === 'succeeded' && state.cacheKey === makeCacheKey(docDate, companyCode, yearCode)) {
        return false; // already holding fresh data for this exact date - skip the re-fetch
      }
      return true;
    },
  }
);

const ewayBillCompareSlice = createSlice({
  name: 'ewayBillCompare',
  initialState: {
    data: [],
    status: 'idle',
    error: null,
    cacheKey: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEwayBillCompareData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchEwayBillCompareData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload.rows;
        state.cacheKey = action.payload.cacheKey;
      })
      .addCase(fetchEwayBillCompareData.rejected, (state, action) => {
        if (action.meta.condition) return; // skipped by cache hit, not a real error
        state.status = 'failed';
        state.error = action.error.message;
        state.data = [];
      });
  },
});

export default ewayBillCompareSlice.reducer;
